import type { PlayerProfile } from './profile';

const baseUrl = (import.meta.env.VITE_APP_API_URL || import.meta.env.VITE_FEEDBACK_API_URL || '') as string;

export type MatchRecord = {
  id: string;
  status: 'WAITING' | 'PLAYING' | 'FINISHED';
  choId: string;
  choHandle: string;
  choRating?: number;
  hanId?: string;
  hanHandle?: string;
  hanRating?: number;
  turn: 'cho' | 'han';
  pieces: unknown[];
  moves: unknown[];
  revision?: number;
  legalMoves?: string[];
  lastMove?: { from: { file: number; rank: number }; to: { file: number; rank: number } };
  ruleEngine?: string;
  timeControl?: { initialMs: number; incrementMs: number; label: string };
  clock?: { choMs: number; hanMs: number; runningSince: number | null };
  result?: string;
  createdAt: string;
};

export type StudyRecord = { id: string; title: string; description: string; authorHandle: string; createdAt: string; chapters: number };
export type TournamentRecord = { id: string; name: string; format: 'ARENA' | 'SWISS'; hostHandle: string; participants: string[]; status: 'OPEN' | 'STARTED' | 'FINISHED'; createdAt: string };
export type BakboRecord = {
  id: string; title: string; summary: string; attacker: 'cho' | 'han'; attackerMoves: number; exact: boolean;
  difficulty: number; tier: string; themes: string[]; pieces: unknown[]; status: 'CROSS_VERIFIED'; contentClass?: 'AUTO_GENERATED_TRAINING'|'CURATED_BAKBO';
  verification: { ruleVersion: string; solverVersion: string; engineVersion: string; nodesSearched: number; winningFirstMoves: number; shorterSolutionExists: boolean; allDefencesCovered: boolean; positionsCrossChecked: number; economical: boolean; proofHash: string; verifiedAt: string };
};

export type TacticRecord = {
  id: string;
  title: string;
  summary: string;
  attacker: 'cho' | 'han';
  attackerMoves: number;
  goalType: 'mate' | 'partial-mate' | 'winning' | 'turnaround' | 'defensive';
  difficulty: number;
  tier: string;
  themes: string[];
  pieces: unknown[];
  previousMove: { side: 'cho' | 'han'; uci: string; from: { file: number; rank: number }; to: { file: number; rank: number } };
  status: 'ENGINE_REVALIDATED';
  contentClass: 'REAL_GAME_TACTIC';
  verification: {
    status: 'ENGINE_REVALIDATED';
    engine: string;
    variant: 'janggi';
    lowDepth: number;
    highDepth: number;
    multiPv: number;
    quality: number;
    difficultySignal: number;
    ambiguity: number;
    positionHash: string;
    artifactHash: string;
  };
};

export type AnalysisLine = {
  depth: number;
  seldepth: number;
  multipv: number;
  nodes: number;
  nps: number;
  pv: string[];
  score: { type: 'cp' | 'mate'; value: number };
};

export type AnalysisResult = {
  engine: string;
  variant: 'janggi';
  ruleProfile: 'FAIRY_STOCKFISH_JANGGI';
  fen: string;
  sideToMove: 'cho' | 'han';
  depth: number;
  bestMove: string | null;
  legalMoves: string[];
  material: { cho: number; han: number };
  lines: AnalysisLine[];
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  if (!baseUrl) throw new Error('API_NOT_CONFIGURED');
  const response = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
    ...init,
    cache: init?.cache ?? (init?.method ? 'default' : 'no-store'),
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
  });
  if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || 'API_ERROR');
  return response.json() as Promise<T>;
};

export const api = {
  configured: Boolean(baseUrl),
  saveProfile: (profile: PlayerProfile) => request<PlayerProfile>(`/profiles/${profile.id}`, { method: 'PUT', body: JSON.stringify(profile) }),
  getProfile: (id: string) => request<PlayerProfile>(`/profiles/${id}`),
  lobby: () => request<{ items: MatchRecord[] }>('/matches'),
  createMatch: (profile: PlayerProfile, timeControl: { initialMinutes: number; incrementSeconds: number } = { initialMinutes: 10, incrementSeconds: 5 }) => request<MatchRecord>('/matches', { method: 'POST', body: JSON.stringify({ player: profile, timeControl }) }),
  joinMatch: (id: string, profile: PlayerProfile) => request<MatchRecord>(`/matches/${id}/join`, { method: 'POST', body: JSON.stringify({ player: profile }) }),
  getMatch: (id: string) => request<MatchRecord>(`/matches/${id}`),
  move: (id: string, playerId: string, from: { file: number; rank: number }, to: { file: number; rank: number }) => request<MatchRecord>(`/matches/${id}/move`, { method: 'POST', body: JSON.stringify({ playerId, from, to }) }),
  pass: (id: string, playerId: string) => request<MatchRecord>(`/matches/${id}/move`, { method: 'POST', body: JSON.stringify({ playerId, isPass: true }) }),
  resign: (id: string, playerId: string) => request<MatchRecord>(`/matches/${id}/resign`, { method: 'POST', body: JSON.stringify({ playerId }) }),
  studies: () => request<{ items: StudyRecord[] }>('/studies'),
  createStudy: (data: { title: string; description: string; author: PlayerProfile }) => request<StudyRecord>('/studies', { method: 'POST', body: JSON.stringify(data) }),
  tournaments: () => request<{ items: TournamentRecord[] }>('/tournaments'),
  createTournament: (data: { name: string; format: 'ARENA' | 'SWISS'; host: PlayerProfile }) => request<TournamentRecord>('/tournaments', { method: 'POST', body: JSON.stringify(data) }),
  joinTournament: (id: string, profile: PlayerProfile) => request<TournamentRecord>(`/tournaments/${id}/join`, { method: 'POST', body: JSON.stringify({ player: profile }) }),
  bakbo: () => request<{ items: BakboRecord[] }>('/bakbo'),
  startBakbo: (id: string) => request<{ id: string; sessionId: string; revision: number; sideToMove: 'cho'|'han'; pieces: unknown[]; legalMoves: string[]; engine: string; variant: 'janggitraditional' }>(`/bakbo/${id}/session`, { method: 'POST', body: '{}' }),
  attemptBakbo: (id: string, sessionId: string, revision: number, from: { file: number; rank: number }, to: { file: number; rank: number }) => request<{ correct: boolean; completed: boolean; defenderMove?: { pieceId: string; from: { file: number; rank: number }; to: { file: number; rank: number } }; revision: number; message: string; legalMoves?: string[] }>(`/bakbo/${id}/attempt`, { method: 'POST', body: JSON.stringify({ sessionId, revision, from, to }) }),
  tactics: () => request<{ items: TacticRecord[]; methodology: { source: string; extractor: string; lowDepth: number; highDepth: number; minimumPrincipalVariationPlies: number } }>('/tactics'),
  startTactic: (id: string) => request<{ id: string; sessionId: string; revision: number; sideToMove: 'cho'|'han'; pieces: unknown[]; legalMoves: string[]; engine: string; variant: 'janggi' }>(`/tactics/${id}/session`, { method: 'POST', body: '{}' }),
  attemptTactic: (id: string, sessionId: string, revision: number, from: { file: number; rank: number }, to: { file: number; rank: number }) => request<{ correct: boolean; completed: boolean; defenderMove?: { pieceId: string; from: { file: number; rank: number }; to: { file: number; rank: number } }; revision: number; message: string; legalMoves?: string[] }>(`/tactics/${id}/attempt`, { method: 'POST', body: JSON.stringify({ sessionId, revision, from, to }) }),
  engineMove: (pieces: unknown[], sideToMove: 'cho' | 'han', elo: number, moveTimeMs: number, signal?: AbortSignal) => request<{ engine: string; variant: 'janggi'; elo: number; bestMove: string | null; line: AnalysisLine | null }>('/engine-move', { method: 'POST', body: JSON.stringify({ pieces, sideToMove, elo, moveTimeMs }), signal }),
  analyse: (pieces: unknown[], sideToMove: 'cho' | 'han', depth: number, multiPv: number, signal?: AbortSignal) => request<AnalysisResult>('/analysis', { method: 'POST', body: JSON.stringify({ pieces, sideToMove, depth, multiPv }), signal }),
};
